import * as Headless from "@headlessui/react";
import clsx from "clsx";
import React, { forwardRef, useMemo } from "react";
import { Link } from "@/components/ui/link/Link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faSparkles } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { DropDown } from "@/components/ui/dropdown";
import { Spinner } from "@/components/ui/spinner";
import {
  hexToRgba,
  createConicBorderStyle,
  createGradientBackground,
  resolveColor,
  getDarkModeColor,
  darkenHexColor,
  getSecondaryColor,
  rgbToHsl,
  hslToRgb,
  luminance,
  getContrastTextColor,
} from "@/utils/colorUtils";

const styles = {
  base: [
    // Base
    "relative inline-flex items-center justify-center gap-2 rounded-xl cursor-pointer",
    // Text and font
    "text-sm font-medium",
    // Focus
    "focus:outline-none",
    // Disabled
    "data-disabled:opacity-50",
    // Z-index handling for children
    "[&>*]:relative [&>*]:z-10 [&>*]:isolate",
  ],
  sizes: {
    sm: "h-8 px-2.5 [&_svg]:size-3.5",
    default: "h-9 px-3 [&_svg]:size-4",
    lg: "h-10 px-4 [&_svg]:size-4",
    smIcon: "size-8 p-0 [&_svg]:size-3.5",
    defaultIcon: "size-9 p-0 [&_svg]:size-4",
    lgIcon: "size-10 p-0 [&_svg]:size-4",
  },
  /** Content-sized control for list rows, text links, and icon actions (use with plain). */
  unstyled: "h-auto min-h-0",
  solid: [
    // Base background and hover
    "bg-[var(--btn-bg)] hover:bg-[var(--btn-hover)] text-[var(--btn-text)]",
    "dark:bg-[var(--btn-bg-dark)] dark:hover:bg-[var(--btn-hover-dark)] dark:text-[var(--btn-text-dark)]",
    // Border
    "border-transparent",
  ],
  outline: [
    // Base
    "border border-slate-950/10 text-slate-950 hover:bg-slate-950/[2.5%]",
    // Dark mode
    "dark:border-white/15 dark:text-white dark:hover:bg-white/5",
  ],
  plain: [
    // Base styles
    "border-transparent text-slate-950 hover:bg-slate-950/5",
    // Dark mode
    "dark:text-white dark:hover:bg-white/10",
  ],
  plainUnstyled: [
    "border-transparent text-slate-950 hover:bg-slate-950/5",
    "dark:text-white dark:hover:bg-white/10",
  ],
  fluid: [
    // Base
    "border-transparent",
    // Text color
    "px-4",
    "text-white",
  ],
  gradient: [
    // Base
    "border-transparent text-white",
    // Will be overridden by inline styles for gradient background
  ],
  colors: {
    "dark/zinc": [
      "text-white [--btn-bg:var(--color-slate-900)] [--btn-hover:var(--color-slate-800)] [--btn-text:white] [--btn-bg-dark:var(--color-slate-600)] [--btn-hover-dark:var(--color-slate-500)] [--btn-text-dark:white]",
    ],
    light: [
      "text-slate-950 [--btn-bg:white] [--btn-hover:var(--color-slate-100)] [--btn-text:var(--color-slate-950)] [--btn-bg-dark:var(--color-slate-800)] [--btn-hover-dark:var(--color-slate-700)] [--btn-text-dark:white]",
    ],
    "dark/white": [
      "text-white [--btn-bg:var(--color-slate-900)] [--btn-hover:var(--color-slate-800)] [--btn-text:white] [--btn-bg-dark:white] [--btn-hover-dark:var(--color-slate-100)] [--btn-text-dark:var(--color-slate-950)]",
    ],
    dark: [
      "text-white [--btn-bg:var(--color-slate-900)] [--btn-hover:var(--color-slate-800)] [--btn-text:white] [--btn-bg-dark:var(--color-slate-800)] [--btn-hover-dark:var(--color-slate-700)] [--btn-text-dark:white]",
    ],
    white: [
      "text-slate-950 [--btn-bg:white] [--btn-hover:var(--color-slate-100)] [--btn-text:var(--color-slate-950)] [--btn-bg-dark:var(--color-slate-800)] [--btn-hover-dark:var(--color-slate-700)] [--btn-text-dark:white]",
    ],
    zinc: [
      "text-slate-700 [--btn-bg:rgba(229,231,235,0.8)] [--btn-hover:rgba(229,231,235,0.9)] [--btn-text:#334155] [--btn-bg-dark:rgba(45,58,81,0.5)] [--btn-hover-dark:rgba(45,58,81,0.7)] [--btn-text-dark:#cbd5e1] border border-slate-200/50 dark:border-slate-700/50",
    ],
    indigo: [
      "text-white [--btn-bg:var(--color-indigo-600)] [--btn-hover:var(--color-indigo-700)] [--btn-text:white] [--btn-bg-dark:var(--color-indigo-500)] [--btn-hover-dark:var(--color-indigo-400)] [--btn-text-dark:white]",
    ],
    cyan: [
      "text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-hover:var(--color-cyan-400)] [--btn-text:var(--color-cyan-950)] [--btn-bg-dark:var(--color-cyan-400)] [--btn-hover-dark:var(--color-cyan-300)] [--btn-text-dark:var(--color-cyan-50)]",
    ],
    red: [
      "text-white [--btn-bg:var(--color-red-600)] [--btn-hover:var(--color-red-700)] [--btn-text:white] [--btn-bg-dark:var(--color-red-400)] [--btn-hover-dark:var(--color-red-300)] [--btn-text-dark:white]",
    ],
    orange: [
      "text-white [--btn-bg:var(--color-orange-500)] [--btn-hover:var(--color-orange-600)] [--btn-text:white] [--btn-bg-dark:var(--color-orange-400)] [--btn-hover-dark:var(--color-orange-300)] [--btn-text-dark:white]",
    ],
    amber: [
      "text-amber-950 [--btn-bg:var(--color-amber-400)] [--btn-hover:var(--color-amber-500)] [--btn-text:var(--color-amber-950)] [--btn-bg-dark:var(--color-amber-500)] [--btn-hover-dark:var(--color-amber-400)] [--btn-text-dark:var(--color-amber-50)]",
    ],
    yellow: [
      "text-yellow-950 [--btn-bg:var(--color-yellow-300)] [--btn-hover:var(--color-yellow-400)] [--btn-text:var(--color-yellow-950)] [--btn-bg-dark:var(--color-yellow-400)] [--btn-hover-dark:var(--color-yellow-300)] [--btn-text-dark:var(--color-yellow-50)]",
    ],
    lime: [
      "text-lime-950 [--btn-bg:var(--color-lime-300)] [--btn-hover:var(--color-lime-400)] [--btn-text:var(--color-lime-950)] [--btn-bg-dark:var(--color-lime-400)] [--btn-hover-dark:var(--color-lime-300)] [--btn-text-dark:var(--color-lime-50)]",
    ],
    green: [
      "text-white [--btn-bg:var(--color-green-600)] [--btn-hover:var(--color-green-700)] [--btn-text:white] [--btn-bg-dark:var(--color-green-500)] [--btn-hover-dark:var(--color-green-400)] [--btn-text-dark:white]",
    ],
    emerald: [
      "text-white [--btn-bg:var(--color-emerald-400)] [--btn-hover:var(--color-emerald-500)] [--btn-text:white] [--btn-bg-dark:var(--color-emerald-400)] [--btn-hover-dark:var(--color-emerald-300)] [--btn-text-dark:white]",
    ],
    teal: [
      "text-white [--btn-bg:var(--color-teal-600)] [--btn-hover:var(--color-teal-700)] [--btn-text:white] [--btn-bg-dark:var(--color-teal-500)] [--btn-hover-dark:var(--color-teal-400)] [--btn-text-dark:white]",
    ],
    sky: [
      "text-white [--btn-bg:var(--color-sky-500)] [--btn-hover:var(--color-sky-600)] [--btn-text:white] [--btn-bg-dark:var(--color-sky-400)] [--btn-hover-dark:var(--color-sky-300)] [--btn-text-dark:white]",
    ],
    blue: [
      "text-white [--btn-bg:var(--color-blue-500)] [--btn-hover:var(--color-blue-600)] [--btn-text:white] [--btn-bg-dark:var(--color-blue-400)] [--btn-hover-dark:var(--color-blue-300)] [--btn-text-dark:white]",
    ],
    violet: [
      "text-white [--btn-bg:var(--color-violet-500)] [--btn-hover:var(--color-violet-600)] [--btn-text:white] [--btn-bg-dark:var(--color-violet-400)] [--btn-hover-dark:var(--color-violet-300)] [--btn-text-dark:white]",
    ],
    purple: [
      "text-white [--btn-bg:var(--color-purple-500)] [--btn-hover:var(--color-purple-600)] [--btn-text:white] [--btn-bg-dark:var(--color-purple-400)] [--btn-hover-dark:var(--color-purple-300)] [--btn-text-dark:white]",
    ],
    fuchsia: [
      "text-white [--btn-bg:var(--color-fuchsia-500)] [--btn-hover:var(--color-fuchsia-600)] [--btn-text:white] [--btn-bg-dark:var(--color-fuchsia-400)] [--btn-hover-dark:var(--color-fuchsia-300)] [--btn-text-dark:white]",
    ],
    pink: [
      "text-white [--btn-bg:var(--color-pink-500)] [--btn-hover:var(--color-pink-600)] [--btn-text:white] [--btn-bg-dark:var(--color-pink-400)] [--btn-hover-dark:var(--color-pink-300)] [--btn-text-dark:white]",
    ],
    rose: [
      "text-white [--btn-bg:var(--color-rose-500)] [--btn-hover:var(--color-rose-600)] [--btn-text:white] [--btn-bg-dark:var(--color-rose-400)] [--btn-hover-dark:var(--color-rose-300)] [--btn-text-dark:white]",
    ],
    slate: [
      "text-white [--btn-bg:var(--color-slate-500)] [--btn-hover:var(--color-slate-600)] [--btn-text:white] [--btn-bg-dark:var(--color-slate-700)] [--btn-hover-dark:var(--color-slate-600)] [--btn-text-dark:white]",
    ],
    color: [
      "text-slate-800 [--btn-bg:#f1f5f9] [--btn-hover:#e2e8f0] [--btn-text:#1e293b] [--btn-bg-dark:rgba(15,23,42,0.2)] [--btn-hover-dark:rgba(15,23,42,0.3)] [--btn-text-dark:white]",
    ],
  },
};

export type ButtonSize = "sm" | "default" | "lg" | "unstyled";

function buttonSizeClasses(size: ButtonSize, iconOnly: boolean): string {
  if (size === "unstyled") {
    return styles.unstyled;
  }
  if (iconOnly) {
    if (size === "sm") return styles.sizes.smIcon;
    if (size === "lg") return styles.sizes.lgIcon;
    return styles.sizes.defaultIcon;
  }
  if (size === "sm") return styles.sizes.sm;
  if (size === "lg") return styles.sizes.lg;
  return styles.sizes.default;
}

// MenuItem type for dropdown items
export type MenuItem =
  | {
      header: string;
      useDefaultCursor?: boolean,
      name?: never;
      href?: never;
      onClick?: never;
      separator?: never;
    }
  | {
      name: string;
      header?: never;
      href?: string;
      onClick?: () => void;
      separator?: never;
      useDefaultCursor?: boolean;
      /** Optional leading icon (e.g. FontAwesome). */
      icon?: React.ReactNode;
    }
  | {
      separator: true;
      header?: never;
      name?: never;
      href?: never;
      onClick?: never;
      useDefaultCursor?: boolean,
    };

type ButtonProps = (
  | {
      color?: keyof typeof styles.colors;
      outline?: never;
      plain?: never;
      fluid?: never;
      gradient?: never;
    }
  | {
      color?: never;
      outline: true;
      plain?: never;
      fluid?: never;
      gradient?: never;
    }
  | {
      color?: never;
      outline?: never;
      plain: true;
      fluid?: never;
      gradient?: never;
    }
  | {
      color?: never;
      outline?: never;
      plain?: never;
      fluid: true;
      gradient?: never;
    }
  | {
      color?: never;
      outline?: never;
      plain?: never;
      fluid?: never;
      gradient: true;
    }
) & {
  className?: string;
  children?: React.ReactNode; // Make children optional
  // Current color prop for dynamic styling
  currentColor?: string;
  // Dropdown specific props
  dropdown?: boolean;
  dropdownItems?: MenuItem[];
  dropdownHeader?: React.ReactNode;
  dropdownIcon?: React.ReactNode;
  /** Screen reader label for the split-button chevron trigger (icon-only). */
  dropdownMenuButtonAriaLabel?: string;
  // Icon prop for normal buttons
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
  // Fluid gradient specific props
  gradientColors?: string[];
  gradientDirection?: "horizontal" | "vertical" | "diagonal";
  // Animation control
  animate?: boolean;
  // Border animation control (matches EditablePill)
  animateBorder?: boolean;
  // Opacity intensity for currentColor styling
  opacityIntensity?: number;
  /** Shows a spinner, disables the control, and sets aria-busy. */
  loading?: boolean;
  /** sm=h-8 (in-card), default=h-9 (page CTAs), lg=h-10 (forms); unstyled for list rows. */
  size?: ButtonSize;
} & (
    | Omit<Headless.ButtonProps, "as" | "className">
    | Omit<React.ComponentPropsWithoutRef<typeof Link>, "className">
  );

export const Button = forwardRef(function Button(
  {
    color,
    outline,
    plain,
    fluid,
    gradient,
    className,
    children,
    currentColor,
    dropdown,
    dropdownItems,
    dropdownHeader,
    dropdownIcon = <FontAwesomeIcon icon={faChevronDown} />,
    dropdownMenuButtonAriaLabel,
    gradientColors = ["#c084fc", "#a7bffa", "#5af4ff"],
    gradientDirection = "horizontal",
    animate = false,
    animateBorder = false,
    opacityIntensity = 0.3,
    icon,
    iconPosition = "left",
    loading = false,
    size = "default",
    ...props
  }: ButtonProps,
  ref: React.ForwardedRef<HTMLElement>
) {
  // Helper function to create conic border style for gradient buttons
  const conicBorderStyle = () => {
    if (!gradient && !fluid) {
      return {};
    }

    if (gradientColors?.length) {
      // If custom colors provided, use the existing gradient logic
      const colorsForBorder = animateBorder
        ? [...gradientColors, "rgba(0,0,0,0)", "rgba(0,0,0,0)"]
        : gradientColors;
      const style = createConicBorderStyle({ colors: colorsForBorder });
      if (animateBorder) {
        return {
          ...style,
          animation: "border-spin-ani 3s ease-in-out infinite",
          "--border-angle": "0turn",
        } as React.CSSProperties;
      }
      return style;
    }

    // Default gray gradient colors and pattern
    const defaultColors = {
      start: "#9CA3AF", // gray-400
      middle: "#4B5563", // gray-600
      end: "#374151", // gray-700
    };

    const initialAngle = 258;
    const fromAngle = animateBorder
      ? `calc(var(--border-angle) + ${initialAngle}deg)`
      : `${initialAngle}deg`;

    const background = `conic-gradient(
      from ${fromAngle},
      ${defaultColors.start} 53deg,
      ${defaultColors.middle} 82deg,
      ${defaultColors.end} 214deg,
      transparent 300deg,
      ${defaultColors.start} 360deg
    )`;

    const baseStyle = {
      position: "absolute",
      inset: 0,
      zIndex: 0,
      mixBlendMode: "screen",
      borderRadius: "0.75rem",
      padding: 1,
      pointerEvents: "none",
      opacity: 0.8,
      background,
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    } as React.CSSProperties;

    if (animateBorder) {
      return {
        ...baseStyle,
        animation: "border-spin-ani 3s ease-in-out infinite",
      } as React.CSSProperties;
    }

    return baseStyle;
  };

  // Helper function to create conic border style for currentColor
  const currentColorConicBorderStyle = useMemo(() => {
    // Default gray gradient colors for the conic border
    const defaultColors = {
      start: "#9CA3AF", // gray-400
      middle: "#4B5563", // gray-600
      end: "#374151", // gray-700
    };

    const baseColor = currentColor ? resolveColor(currentColor) : null;
    const colors = baseColor
      ? {
          start: darkenHexColor(baseColor, -0.1),
          middle: baseColor,
          end: getSecondaryColor(baseColor),
        }
      : defaultColors;

    const initialAngle = 258; // Match the example angle
    const fromAngle = animate
      ? `calc(var(--border-angle) + ${initialAngle}deg)`
      : `${initialAngle}deg`;

    const background = `conic-gradient(
      from ${fromAngle},
      ${colors.start} 53deg,
      ${colors.middle} 82deg,
      ${colors.end} 214deg,
      transparent 300deg,
      ${colors.start} 360deg
    )`;

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      inset: -4,
      zIndex: 0,
      mixBlendMode: "screen",
      borderRadius: "0.75rem",
      padding: 1,
      pointerEvents: "none",
      opacity: 0.8,
      background,
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    };

    if (animateBorder) {
      return {
        ...baseStyle,
        animation: "border-spin-ani 3s ease-in-out infinite",
      };
    }

    return baseStyle;
  }, [currentColor, animateBorder]);

  // Generate gradient background style for currentColor
  const currentColorGradientStyle = useMemo(() => {
    if (!currentColor) {
      // Return empty object since we're using the base solid styles for default
      return {};
    }

    const resolvedHex = resolveColor(currentColor);
    if (!resolvedHex) {
      return {};
    }

    const isDarkMode =
      typeof window !== "undefined" &&
      document.documentElement.classList.contains("dark");
    const baseColor = isDarkMode ? getDarkModeColor(resolvedHex) : resolvedHex;

    const gradientStart = darkenHexColor(baseColor, -0.1);
    const gradientEnd = getSecondaryColor(baseColor);
    const opacity = Math.max(0, Math.min(1, opacityIntensity));
    const gradientAngle = 206;

    return {
      background: `linear-gradient(${gradientAngle}deg, 
        color-mix(in srgb, ${gradientStart} ${opacity * 100}%, transparent) 0%, 
        color-mix(in srgb, ${baseColor} ${opacity * 100}%, transparent) 50%, 
        color-mix(in srgb, ${gradientEnd} ${
          opacity * 100
        }%, transparent) 100%)`,
    };
  }, [currentColor, opacityIntensity]);

  // Helper function to get color value from color prop
  const getColorValue = (colorName: keyof typeof styles.colors): string => {
    const colorMap: Record<keyof typeof styles.colors, string> = {
      "dark/zinc": "#475569", // slate-600
      light: "#ffffff",
      "dark/white": "#1f2937", // slate-800
      dark: "#1f2937", // slate-800
      white: "#ffffff",
      zinc: "#e5e7eb", // slate-200
      indigo: "#4f46e5", // indigo-600 (matches SegmentedPillToggle active pill)
      cyan: "#22d3ee", // cyan-400
      red: "#dc2626", // red-600
      orange: "#f97316", // orange-500
      amber: "#fbbf24", // amber-400
      yellow: "#fde047", // yellow-300
      lime: "#84cc16", // lime-400
      green: "#16a34a", // green-600
      emerald: "#34d399", // emerald-400
      teal: "#0d9488", // teal-600
      sky: "#0ea5e9", // sky-500
      blue: "#3b82f6", // blue-500
      violet: "#8b5cf6", // violet-500
      purple: "#a855f7", // purple-500
      fuchsia: "#d946ef", // fuchsia-500
      pink: "#ec4899", // pink-500
      rose: "#f43f5e", // rose-500
      slate: "#64748b", // slate-500
      color: "#0f172a", // slate-900 (dark color for the "color" variant)
    };
    return colorMap[colorName];
  };

  // Helper function to create conic border style for color prop
  const colorConicBorderStyle = () => {
    if (!color || color === "zinc") {
      return {};
    }

    // Use transparent color for the "color" variant, and main color for others
    let baseColor: string;
    if (color === "color") {
      baseColor = "rgba(15,23,42,0.2)";
    } else {
      baseColor = getColorValue(color);
    }

    const fromAngle = animateBorder
      ? `calc(var(--border-angle) + 206deg)`
      : "206deg";

    let background;
    if (animateBorder) {
      background = `conic-gradient(
        from ${fromAngle},
        transparent 0deg,
        ${baseColor} 30deg,
        ${baseColor} 75deg,
        ${baseColor} 135deg,
        ${baseColor} 180deg,
        transparent 225deg
      )`;
    } else {
      background = `conic-gradient(
        from ${fromAngle},
        ${baseColor} 0deg,
        ${baseColor} 120deg,
        transparent 180deg,
        transparent 300deg,
        ${baseColor} 360deg
      )`;
    }

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      inset: -4, // Offset by 4px to create the border effect
      zIndex: 0,
      mixBlendMode: "normal",
      borderRadius: "1rem", // Slightly larger to account for the 4px offset
      padding: 2, // border width
      pointerEvents: "none",
      opacity: 1,
      background,
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    };

    if (animateBorder) {
      return {
        ...baseStyle,
        animation: "border-spin-ani 3s ease-in-out infinite",
      };
    }

    return baseStyle;
  };

  // Helper function to create conic border style for zinc color
  const zincConicBorderStyle = () => {
    if (color !== "zinc") {
      return {};
    }

    // Use the same default gray color as EditablePill
    const baseColor = "#e5e7eb";
    const gradientStart = darkenHexColor(baseColor, -0.1);
    const gradientEnd = getSecondaryColor(baseColor);

    const fromAngle = animateBorder
      ? `calc(var(--border-angle) + 206deg)`
      : "206deg";

    let background;
    if (animateBorder) {
      background = `conic-gradient(
        from ${fromAngle},
        transparent 0deg,
        ${gradientStart} 45deg,
        ${baseColor} 120deg,
        ${gradientEnd} 240deg,
        transparent 315deg
      )`;
    } else {
      background = `conic-gradient(
        from ${fromAngle},
        ${gradientStart} 0deg,
        ${baseColor} 120deg,
        ${gradientEnd} 240deg,
        transparent 300deg,
        ${gradientStart} 360deg
      )`;
    }

    const baseStyle: React.CSSProperties = {
      position: "absolute",
      inset: 0,
      zIndex: 0,
      mixBlendMode: "screen",
      borderRadius: "0.75rem",
      padding: 1, // border width
      pointerEvents: "none",
      opacity: 0.8,
      background,
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
      WebkitMask:
        "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
    };

    if (animateBorder) {
      return {
        ...baseStyle,
        animation: "border-spin-ani 3s ease-in-out infinite",
      };
    }

    return baseStyle;
  };

  // Generate gradient background style for zinc color
  const zincGradientStyle =
    color === "zinc"
      ? (() => {
          const baseColor = "#e5e7eb";
          const gradientStart = darkenHexColor(baseColor, -0.1);
          const gradientEnd = getSecondaryColor(baseColor);
          const opacity = 0.3; // Similar to EditablePill's opacityIntensity

          return {
            background: `linear-gradient(206deg, 
              color-mix(in srgb, ${gradientStart} ${
                opacity * 100
              }%, transparent) 0%, 
              color-mix(in srgb, ${baseColor} ${
                opacity * 100
              }%, transparent) 50%, 
              color-mix(in srgb, ${gradientEnd} ${
                opacity * 100
              }%, transparent) 100%)`,
          };
        })()
      : {};

  const isUnstyled = size === "unstyled";
  const isIconOnly = Boolean(icon && !children);

  // Generate the style classes based on button type
  const styleClasses = fluid
    ? styles.fluid
    : gradient
      ? styles.gradient
      : outline
        ? styles.outline
        : plain
          ? isUnstyled
            ? styles.plainUnstyled
            : styles.plain
          : clsx(styles.solid, styles.colors[color || "slate"]);

  const buttonClasses = clsx(
    styles.base,
    buttonSizeClasses(size, isIconOnly),
    styleClasses,
    className,
  );

  const stretchFullWidth = className?.includes("w-full");
  const stretchFullHeight = className?.includes("h-full");
  const stretchClasses = clsx(
    stretchFullWidth && "min-w-0 w-full flex-1",
    stretchFullHeight && "h-full",
  );
  const wrapperStretchClasses = clsx(
    stretchFullWidth ? "flex w-full" : "inline-flex",
    stretchFullHeight && "h-full",
    className?.includes("shrink-0") && "shrink-0",
  );

  // Generate gradient background style for gradient buttons
  const gradientStyle = gradient
    ? createGradientBackground({
        colors: gradientColors,
        direction: gradientDirection,
        alpha: 0.18,
      })
    : {};

  // Combine gradient styles - currentColor takes precedence over zinc, which takes precedence over regular gradient
  const combinedGradientStyle = currentColor
    ? currentColorGradientStyle
    : color === "zinc"
      ? zincGradientStyle
      : gradientStyle;

  // Generate gradient text style for fluid buttons
  const gradientTextStyle = fluid
    ? {
        background: `linear-gradient(${
          gradientDirection === "vertical"
            ? "to bottom"
            : gradientDirection === "diagonal"
              ? "135deg"
              : "to right"
        }, ${gradientColors.join(", ")})`,
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        color: "transparent",
        WebkitTextFillColor: "transparent",
        display: "inline-block",
      }
    : {};

  const content = fluid ? (
    <span style={gradientTextStyle} className="flex items-center space-x-2">
      <FontAwesomeIcon icon={faSparkles} style={{ color: gradientColors[0] }} />
      <span>{children}</span>
    </span>
  ) : icon ? (
    <span className="flex items-center gap-2">
      {iconPosition === "left" && icon}
      {children && <span>{children}</span>}
      {iconPosition === "right" && icon}
    </span>
  ) : (
    children
  );

  const isDisabled =
    loading || Boolean("disabled" in props ? props.disabled : false);

  const renderedContent = loading ? (
    <span className="inline-flex items-center gap-2">
      <Spinner
        size="sm"
        className="shrink-0"
        trackColor="color-mix(in srgb, currentColor 35%, transparent)"
        indicatorColor="currentColor"
      />
      {content}
    </span>
  ) : (
    content
  );

  // Create the button element based on whether it's a link or regular button
  const mainButton =
    "href" in props ? (
      <Link
        {...props}
        className={clsx(
          buttonClasses,
          stretchClasses,
          dropdownItems?.length ? "rounded-r-none border-r-0 focus:ring-0" : "",
          gradient || fluid || currentColor ? "relative z-10" : ""
        )}
        style={combinedGradientStyle}
        ref={ref as React.ForwardedRef<HTMLAnchorElement>}
      >
        {renderedContent}
      </Link>
    ) : (
      <Headless.Button
        {...props}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        className={clsx(
          buttonClasses,
          stretchClasses,
          dropdownItems?.length ? "rounded-r-none border-r-0 focus:ring-0" : "",
          gradient || fluid || currentColor ? "relative z-10" : ""
        )}
        style={combinedGradientStyle}
        ref={ref}
      >
        {renderedContent}
      </Headless.Button>
    );

  const toggleButton = (
    <button
      type="button"
      className={clsx(
        buttonClasses,
        "rounded-l-none shrink-0 px-2 focus:ring-0",
        outline
          ? "border-l border-l-slate-950/10 dark:border-l-white/15"
          : "border-l border-l-white/10 text-xs",
        gradient || fluid || currentColor ? "relative z-10" : ""
      )}
      style={combinedGradientStyle}
    >
      {dropdownIcon}
    </button>
  );

  // Only render dropdown structure if there are dropdown items
  if (!dropdownItems?.length) {
    // Always wrap with conic border
    return (
      <span
        className={clsx("relative rounded-xl", wrapperStretchClasses)}
      >
        {/* Add animation CSS for conic border spin */}
        {animateBorder && (
          <style>{`
            @property --border-angle {
              syntax: '<angle>';
              inherits: false;
              initial-value: 0turn;
            }
            @keyframes border-spin-ani {
              to { --border-angle: 1turn; }
            }
          `}</style>
        )}
        {animateBorder && (
          <div
            style={
              currentColor
                ? currentColorConicBorderStyle
                : color === "zinc"
                  ? zincConicBorderStyle()
                  : color
                    ? colorConicBorderStyle()
                    : conicBorderStyle()
            }
            className="rounded-xl"
          />
        )}
        {mainButton}
      </span>
    );
  }

  // Check if there's no content (no children and no icon)
  const hasContent = children || icon;

  // If dropdown has no content, render just the chevron button wrapped in DropDown
  if (!hasContent) {
    // Filter out Link-specific props for button-only rendering
    const buttonProps = "href" in props ? {} : props;

    const chevronButton = (
      <Headless.Button
        {...buttonProps}
        className={clsx(
          buttonClasses,
          "px-2 focus:ring-0 text-xs",
          gradient || fluid || currentColor ? "relative z-10" : ""
        )}
        style={combinedGradientStyle}
        ref={ref}
      >
        {dropdownIcon}
      </Headless.Button>
    );

    return (
      <div
        className={clsx(
          className?.includes("w-full") ? "flex" : "inline-flex",
          "rounded-md relative group focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-slate-700",
          "rounded-xl"
        )}
      >
        {/* Add animation CSS for conic border spin */}
        {animateBorder && (
          <style>{`
            @property --border-angle {
              syntax: '<angle>';
              inherits: false;
              initial-value: 0turn;
            }
            @keyframes border-spin-ani {
              to { --border-angle: 1turn; }
            }
          `}</style>
        )}
        {animateBorder && (
          <div
            style={
              currentColor
                ? currentColorConicBorderStyle
                : color === "zinc"
                  ? zincConicBorderStyle()
                  : color
                    ? colorConicBorderStyle()
                    : conicBorderStyle()
            }
            className="rounded-xl"
          />
        )}
        <DropDown
          label={chevronButton}
          width="w-max min-w-[14rem]"
          menuButtonAriaLabel={dropdownMenuButtonAriaLabel}
        >
          <div className="p-1.5">
            {dropdownHeader && (
              <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                {dropdownHeader}
              </div>
            )}
            {dropdownItems?.map((item, index) => (
              <div key={index}>
                {item.header ? (
                  <div className="px-3 py-2 text-sm font-medium text-slate-900 dark:text-white">
                    {item.header}
                  </div>
                ) : item.separator ? (
                  <div className="my-1 h-px bg-slate-200 dark:bg-slate-700" />
                ) : (
                  <Headless.MenuItem key={item.name}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={item.onClick}
                        className={clsx(
                          "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm whitespace-nowrap text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700",
                          active ? "bg-slate-100 dark:bg-slate-700" : "",
                        )}
                      >
                        {"icon" in item && item.icon ? (
                          <span className="flex h-3.5 w-4 shrink-0 justify-center text-slate-500 dark:text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                            {item.icon}
                          </span>
                        ) : null}
                        <span>{item.name}</span>
                      </button>
                    )}
                  </Headless.MenuItem>
                )}
              </div>
            ))}
          </div>
        </DropDown>
      </div>
    );
  }

  // For dropdown with content, we use the DropDown component
  return (
    <div
      className={clsx(
        wrapperStretchClasses,
        "rounded-md relative group focus-within:ring-2 focus-within:ring-indigo-400 focus-within:ring-offset-2 focus-within:ring-offset-white dark:focus-within:ring-offset-slate-800",
        "rounded-xl"
      )}
    >
      {/* Add animation CSS for conic border spin */}
      {animateBorder && (
        <style>{`
            @property --border-angle {
              syntax: '<angle>';
              initial-value: 0turn;
            }
            @keyframes border-spin-ani {
              to { --border-angle: 1turn; }
            }
          `}</style>
      )}
      {animateBorder && (
        <div
          style={
            currentColor
              ? currentColorConicBorderStyle
              : color === "zinc"
                ? zincConicBorderStyle()
                : color
                  ? colorConicBorderStyle()
                  : conicBorderStyle()
          }
          className="rounded-xl"
        />
      )}
      {mainButton}
      <DropDown
        label={toggleButton}
        width="w-48"
        menuButtonAriaLabel={dropdownMenuButtonAriaLabel}
      >
        <div className="py-1 px-1">
          {dropdownHeader && (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
              {dropdownHeader}
            </div>
          )}
          {dropdownItems?.map((item, index) => {
            if (item.separator) {
              return (
                <div
                  key={`separator-${index}`}
                  className="my-1 h-px bg-slate-200 dark:bg-slate-700"
                />
              );
            }
            if (item.header) {
              return (
                <div
                  key={item.header}
                  className={`px-3 py-2 text-sm font-medium text-slate-900 dark:text-white ${item.useDefaultCursor ? "cursor-none": null}`}
                >
                  {item.header}
                </div>
              );
            }
            const menuItemContent = (
              <Headless.MenuItem key={item.name}>
                {({ active }) => (
                  <button
                    type="button"
                    onClick={item.onClick}
                    className={clsx(
                      "flex w-full cursor-pointer items-center gap-2 rounded-xl bg-transparent px-3 py-1.5 text-left text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800/50",
                      active ? "bg-slate-100 dark:bg-slate-700" : "",
                    )}
                  >
                    {"icon" in item && item.icon ? (
                      <span className="flex h-3.5 w-4 shrink-0 justify-center text-slate-500 dark:text-slate-400 [&>svg]:h-3.5 [&>svg]:w-3.5">
                        {item.icon}
                      </span>
                    ) : null}
                    <span className="min-w-0">{item.name}</span>
                  </button>
                )}
              </Headless.MenuItem>
            );

            return item.href ? (
              <Link key={item.name} href={item.href}>
                {menuItemContent}
              </Link>
            ) : (
              menuItemContent
            );
          })}
        </div>
      </DropDown>
    </div>
  );
});

/**
 * Expand the hit area to at least 44×44px on touch devices
 */
export function TouchTarget({ children }: { children: React.ReactNode }) {
  return (
    <>
      <span
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 [@media(pointer:fine)]:hidden"
        aria-hidden="true"
      />
      {children}
    </>
  );
}
